import React, { Component } from "react";
import { graphql } from "gatsby";
import styled, { css } from "styled-components";
import Img from "gatsby-image";
import {
  Row,
  Col,
  Gap,
  Text,
  TextNice,
  TitleNice,
  Section,
  Title,
  Button,
  VH,
  HpTop,
  Fill,
  BusinessTripSection,
  HopSection,
  BeachSection,
  SEO,
} from "../components";
import { up, addResponsivity } from "../lib/styles";

import multicopterImageSrc from "../data/images/usage/multicopter-urban-air-mobility-short-range.png";
import zuriImageSrc from "../data/images/usage/zuri-hybrid-vtol-medium-range-cities.png";
import planeImageSrc from "../data/images/usage/airliner-long-range-commercial-intercontinental.png";

import {
  PassengerUsesBlock,
  CargoUsesBlock,
  SARUsesBlock,
  DefenseUsesBlock,
  PassengerConfig,
  CargoConfig,
  SARConfig,
  DefenseConfig,
} from "../components/usesVideos";

const PlaneImageWrapper = styled.div`
  /* height: 160px; */
  height: 240px;
  display: flex;
  align-items: center;

  justify-content: center;
  img {
    max-width: 100%;
  }
`;

const HorizontalLine = styled.div`
  height: 1px;
  width: 100%;
  background-color: #000000;
  margin: 48px auto;
`;

const Divider = () => (
  <div style={{ background: "#FFFFFF" }}>
    <HorizontalLine />
  </div>
);

const InlineHeader = TitleNice.InlineHeader;

const GapM = () => <Gap gap="60px" mobileGap="40px" bp="tablet" />;
const GapL = () => <Gap gap="80px" mobileGap="40px" bp="tablet" />;
const SectionGap = () => <Gap gap="80px" mobileGap="64px" bp="tablet" />;
const GapXL = () => <Gap gap="100px" mobileGap="40px" bp="tablet" />;

const UsesTitle = styled(Title.Thin).attrs({})`
  text-transform: uppercase;
  text-align: center;
  margin: 0px auto 40px auto;
  font-weight: 700;
`;
const CONTENT_WIDTH = "1000px";

const TextContent = ({ children }) => (
  <Row justifyContent="center">
    <Col maxWidth={CONTENT_WIDTH} bp="tablet " alignItems="center">
      {/* <TextNice> */}
      {children}
      {/* </TextNice> */}
    </Col>
  </Row>
);

const ImagesBlock = ({
  image1,
  image2,
  image3,
  image1Alt,
  image2Alt,
  image3Alt,
}) => (
  <Row
    justifyContent="space-evenly"
    mobileJustifyContent="center"
    alignItems="flex-end"
    mobileAlignItems="center"
    width="100%"
    margin="auto"
    mobileFlexDirection="column"
  >
    <Col width="100%">
      <Img fluid={image1.image.fluid} alt={image1Alt} />
    </Col>
    <Gap gap="16px" />
    <Col width="100%">
      <Img fluid={image2.image.fluid} alt={image2Alt} />
    </Col>
    <Gap gap="16px" />
    <Col width="100%">
      <Img fluid={image3.image.fluid} alt={image3Alt} />
    </Col>
  </Row>
);

const createVideoObject = ({
  siteUrl,
  videoUrl,
  name,
  description,
  thumbnailUrl,
  uploadDate,
}) => {
  if (!siteUrl || !videoUrl) return null;

  const contentUrl = `${siteUrl}${videoUrl}`;

  return {
    "@type": "VideoObject",
    "@context": "https://schema.org",
    name: name,
    description,
    thumbnailUrl,
    contentUrl,
    uploadDate,
  };
};

const buildUsesVideoSchema = (siteUrl) => {
  const getSchemaObject = (config) => {
    return createVideoObject({
      siteUrl,
      videoUrl: config.videoUrl,
      thumbnailUrl: config.placeholderImage,
      description: config.videoDescription,
      name: config.name,
      uploadDate: "2026-03-05",
    });
  };
  const videos = [
    getSchemaObject(PassengerConfig),
    getSchemaObject(CargoConfig),
    getSchemaObject(SARConfig),
    getSchemaObject(DefenseConfig),
  ].filter(Boolean);

  if (!videos.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@graph": videos,
  };
};

class Usage extends Component {
  render() {
    const {
      location: { pathname },
      data: {
        businessTripImage,
        hopImage,
        beachImage,
        cargo1Image,
        cargo2Image,
        cargo3Image,
        sar1Image,
        sar2Image,
        sar3Image,
        passenger1Image,
        passenger2Image,
        passenger3Image,
        defense1Image,
        defense2Image,
        defense3Image,
        site,
      },
    } = this.props;

    const siteUrl = site?.siteMetadata?.url || "";

    return (
      <>
        <SEO
          title="VTOL Use Cases | Zuri"
          description="Explore Hybrid VTOL use cases including passenger travel, cargo delivery, emergency transport, and defense operations, offering fast and flexible point-to-point regional air mobility."
          pathname={pathname}
          videoSchema={buildUsesVideoSchema(siteUrl)}
        />
        <Gap.Top />

        {/* <PassengerUsesVideo />

        <HemsSarUsesVideo />
        <DefenseUsesVideo />
        <CargoUsesVideo /> */}

        <Gap gap="140px" mobileGap="40px" />
        <Section background="black">
          <Title.Big
            innerAs="h1"
            fontWeight="400"
            white
            black={false}
            uppercase
            textAlign="center"
          >
            Hybrid VTOL Use Cases
          </Title.Big>
        </Section>
        <Gap gap="80px" mobileGap="40px" />

        <Section.Simple>
          <Gap gap="60px" mobileGap="48px" />
          <UsesTitle>PASSENGER TRANSPORT</UsesTitle>

          <ImagesBlock
            image1={passenger1Image}
            image1Alt="Zuri Hybrid VTOL cruising over rural landscape during regional passenger flight."
            image2={passenger2Image}
            image2Alt="Business traveler walking toward a Zuri Hybrid VTOL at a terminal."
            image3={passenger3Image}
            image3Alt="Family boarding a Zuri Hybrid VTOL parked on a coastal helipad near private property."
          />
          <GapM />
        </Section.Simple>
        <Section>
          <TextContent>
            <TextNice>
              <InlineHeader as="h3">Efficient Regional Mobility.</InlineHeader>{" "}
              Zuri Hybrid VTOL enables fast and direct travel between regional
              destinations without relying on major airports or runways. Its
              vertical take-off and landing capability allows passengers to
              depart closer to city centers, private properties, business hubs,
              and remote areas, reducing overall travel time and eliminating
              long transfers.
            </TextNice>
            <TextNice noMargin>
              With its hybrid propulsion system, Zuri combines low noise and
              efficiency with extended range, supporting reliable operations for
              personal travel, private charter, tourism, and business mobility.
              The aircraft’s cabin can be configured to meet different passenger
              needs, offering a comfortable and modern flight experience suited
              to regional point-to-point transportation.
            </TextNice>
          </TextContent>
          <GapXL />
        </Section>

        <PassengerUsesBlock />

        <Section.Simple>
          <SectionGap />
          <UsesTitle>Cargo Transport</UsesTitle>

          <ImagesBlock
            image1={cargo1Image}
            image1Alt="Zuri Hybrid VTOL cargo aircraft flying above a dense urban cityscape."
            image2={cargo2Image}
            image2Alt="Ground crew loading low-emission cargo into a Zuri Hybrid VTOL aircraft."
            image3={cargo3Image}
            image3Alt="Zuri Hybrid VTOL cargo aircraft approaching a waterfront city center for delivery."
          />
          <GapM />
        </Section.Simple>
        <Section>
          <TextContent>
            <TextNice>
              <InlineHeader as="h3">On-Demand Delivery.</InlineHeader> Zuri
              Hybrid VTOL supports regional cargo delivery without the need for
              runway infrastructure, taking off and landing in confined or
              hard-to-reach areas. This capability allows direct access to both
              dense metropolitan zones and remote locations, improving delivery
              speed and operational flexibility.
            </TextNice>
            <TextNice noMargin>
              The aircraft’s hybrid propulsion provides reliable performance
              with lower energy consumption, making it suitable for transporting
              valuable, time-sensitive, or specialized cargo. Easy ground
              handling and rapid loading help streamline logistics, enabling
              efficient and cost-effective regional transport operations.
            </TextNice>
          </TextContent>
          <GapXL />
        </Section>

        <CargoUsesBlock />

        <Section.Simple>
          <SectionGap />
          <UsesTitle>EMERGENCY TRANSPORT</UsesTitle>

          <ImagesBlock
            image1={sar1Image}
            image1Alt="Zuri Hybrid VTOL search and rescue aircraft flying above a rocky coastal area."
            image2={sar2Image}
            image2Alt="Emergency responders assisting a patient beside a Zuri Hybrid VTOL in heavy snow."
            image3={sar3Image}
            image3Alt="Rescue crew hoisting an injured person into a Zuri Hybrid VTOL during mountain mission."
          />
          <GapM />
        </Section.Simple>
        <Section>
          <TextContent>
            <TextNice>
              <InlineHeader as="h3">Medevac & SAR Response.</InlineHeader> Zuri
              Hybrid VTOL enhances emergency air operations by reaching areas
              that traditional aircraft cannot access. Its vertical take-off and
              landing capabilities allow for rapid response in remote, urban, or
              difficult environments, supporting missions such as medical
              evacuation and search and rescue.
            </TextNice>
            <TextNice noMargin>
              The interior can be configured for patient transport, onboard
              medical support, and rescue equipment. Its hybrid propulsion
              offers greater range and operational efficiency than fully
              electric systems, helping emergency services deploy more quickly
              and operate for longer durations while maintaining lower overall
              costs.
            </TextNice>
          </TextContent>
          <GapXL />
        </Section>

        <SARUsesBlock />

        <Section.Simple>
          <SectionGap />
          <UsesTitle>DEFENSE & SECURITY</UsesTitle>

          <ImagesBlock
            image1={defense1Image}
            image1Alt="Deck crew on a ship guiding an approaching Zuri Hybrid VTOL during landing operations at sea."
            image2={defense2Image}
            image2Alt="Two Zuri Hybrid VTOL aircraft flying above thick dark smoke during a large-scale response operation."
            image3={defense3Image}
            image3Alt="Zuri Hybrid VTOL hovering over a desert base with open cargo door, near another helicopter and vehicles."
          />
          <GapM />
        </Section.Simple>
        <Section>
          <TextContent>
            <TextNice>
              <InlineHeader as="h3">Rapid Tactical Mobility.</InlineHeader> Zuri
              Hybrid VTOL provides efficient mobility for non-lethal defense and
              security missions requiring fast deployment without the need for
              runways. It can support border patrol, law-enforcement, and
              government operations by accessing confined or
              infrastructure-limited environments quickly and quietly.
            </TextNice>
            <TextNice noMargin>
              The aircraft can be configured for personnel transport, equipment
              delivery, or surveillance support. Its hybrid propulsion enables
              longer mission endurance and cost-efficient operation, giving
              agencies greater flexibility for tactical movement, observation,
              and emergency response without relying on traditional airfields.
            </TextNice>
          </TextContent>

          <GapXL />
        </Section>

        <DefenseUsesBlock />

        <Section>
          <GapM />

          <Row justifyContent="center">
            <Button.ChevronLinkButton
              asA
              href="https://zuri.com/uses/security-defense/hybrid-vtol-aircraft-defense-security-relief"
            >
              Defense & Security Missions
            </Button.ChevronLinkButton>
          </Row>

          <Gap gap="100px" mobileGap="40px" bp="tablet" />
        </Section>

        <Section background="#000000" width="1600px">
          <Col alignItems="center">
            <Gap gap="100px" mobileGap="60px" bp="tablet" />
            <Title.Usage mobileFontSize="26px" as="div">
              TYPICAL USE CASES
            </Title.Usage>
            <Gap gap="60px" mobileGap="0" bp="tablet" />
            <Row
              justifyContent="space-evenly"
              width="100%"
              wrap="nowrap"
              mobileFlexDirection="column"
            >
              <Col basis="33%">
                <PlaneImageWrapper>
                  <img
                    src={multicopterImageSrc}
                    alt="Multicopter aircraft designed for short-range urban air mobility, hovering in place with multiple rotors."
                  />
                </PlaneImageWrapper>
                <Title.UsagePlaneType white>MULTICOPTERS</Title.UsagePlaneType>
                <Gap gap="15px" />
                <Text.UseCaseDescription>
                  Urban Air Mobility, short range
                </Text.UseCaseDescription>
              </Col>
              <Gap gap="10px" mobileGap="80px" />
              <Col basis="33%">
                <PlaneImageWrapper>
                  <img
                    src={zuriImageSrc}
                    alt="Zuri Hybrid VTOL aircraft designed for medium-range intercity flights, combining vertical takeoff with aerodynamic cruising."
                  />
                </PlaneImageWrapper>
                <Title.UsagePlaneType white>ZURI</Title.UsagePlaneType>
                <Gap gap="15px" />
                <Text.UseCaseDescription>
                  Between cities, medium range
                </Text.UseCaseDescription>
              </Col>
              <Gap gap="10px" mobileGap="80px" />
              <Col basis="33%">
                <PlaneImageWrapper>
                  <img
                    src={planeImageSrc}
                    alt="Large commercial airliner suited for long-range and intercontinental travel, flying at high altitude"
                  />
                </PlaneImageWrapper>
                <Title.UsagePlaneType white>AIRLINERS</Title.UsagePlaneType>
                <Gap gap="15px" />
                <Text.UseCaseDescription>
                  Long range, even intercontinental
                </Text.UseCaseDescription>
              </Col>
            </Row>
          </Col>
          <Gap gap="150px" mobileGap="50px" />
        </Section>
      </>
    );
  }
}

export default Usage;

export const pageQuery = graphql`
  fragment UsesImage on File {
    image: childImageSharp {
      fluid(maxWidth: 629, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }

  query {
    passenger1Image: file(
      relativePath: {
        eq: "images/usage/passenger/zuri-hybrid-vtol-regional-flight-over-landscape.jpg"
      }
    ) {
      ...UsesImage
    }
    passenger2Image: file(
      relativePath: {
        eq: "images/usage/passenger/zuri-hybrid-vtol-passenger-business-terminal.jpg"
      }
    ) {
      ...UsesImage
    }
    passenger3Image: file(
      relativePath: {
        eq: "images/usage/passenger/zuri-hybrid-vtol-private-landing-coastal-property.jpg"
      }
    ) {
      ...UsesImage
    }
    cargo1Image: file(
      relativePath: {
        eq: "images/usage/cargo/zuri-hybrid-vtol-cargo-flight-over-city.jpg"
      }
    ) {
      ...UsesImage
    }
    cargo2Image: file(
      relativePath: {
        eq: "images/usage/cargo/zuri-hybrid-vtol-cargo-loading-ground-handling.jpg"
      }
    ) {
      ...UsesImage
    }
    cargo3Image: file(
      relativePath: {
        eq: "images/usage/cargo/zuri-hybrid-vtol-cargo-delivery-urban-waterfront.jpg"
      }
    ) {
      ...UsesImage
    }

    sar1Image: file(
      relativePath: {
        eq: "images/usage/SAR/zuri-hybrid-vtol-search-and-rescue-coastal-flight.jpg"
      }
    ) {
      ...UsesImage
    }
    sar2Image: file(
      relativePath: {
        eq: "images/usage/SAR/zuri-hybrid-vtol-snow-rescue-ground-operation.jpg"
      }
    ) {
      ...UsesImage
    }
    sar3Image: file(
      relativePath: {
        eq: "images/usage/SAR/zuri-hybrid-vtol-mountain-rescue-hoist-operation.jpg"
      }
    ) {
      ...UsesImage
    }

    defense1Image: file(
      relativePath: {
        eq: "images/usage/defense_and_security/zuri-hybrid-vtol-ship-landing-flight-operations.jpg"
      }
    ) {
      ...UsesImage
    }

    defense2Image: file(
      relativePath: {
        eq: "images/usage/defense_and_security/zuri-hybrid-vtol-response-operation-above-smoke.jpg"
      }
    ) {
      ...UsesImage
    }
    defense3Image: file(
      relativePath: {
        eq: "images/usage/defense_and_security/zuri-hybrid-vtol-desert-base-logistics-operations.jpg"
      }
    ) {
      ...UsesImage
    }

    businessTripImage: file(
      relativePath: {
        eq: "images/usage/zuri-vs-airliner-geneva-to-monaco-travel-time-comparison.jpg"
      }
    ) {
      ...FullHdImage
    }
    hopImage: file(
      relativePath: {
        eq: "images/usage/zuri-vs-ferry-phuket-to-phi-phi-travel-comparison.jpg"
      }
    ) {
      ...FullHdImage
    }
    beachImage: file(
      relativePath: {
        eq: "images/usage/zuri-vs-car-airliner-prague-to-cres-travel-comparison.jpg"
      }
    ) {
      ...FullHdImage
    }
    site {
      siteMetadata {
        url
      }
    }
  }
`;
